/**
 * AuditLogPage — read-only audit trail viewer.
 *
 * Lists audit_logs entries with filters (action, resource type, user,
 * date range, free-text search) and pagination. Org admins+ only.
 *
 * Each row shows: timestamp, user, action, resource, IP, and a
 * collapsible JSON details block (every CRUD/score/attendance write
 * stores its diff there).
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Search, Filter, X, ChevronDown, ChevronRight, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const ALL = '__all__';
const PAGE = 50;

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function actionTone(action) {
  if (!action) return 'bg-slate-100 text-slate-700';
  if (action.includes('delete') || action.includes('deleted') || action.includes('failed')) {
    return 'bg-rose-100 text-rose-700';
  }
  if (action.includes('create') || action.includes('scored') || action.includes('imported')) {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (action.includes('login') || action.includes('logout')) {
    return 'bg-sky-100 text-sky-700';
  }
  if (action.includes('update') || action.includes('marked') || action.includes('reordered')) {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-slate-100 text-slate-700';
}

export default function AuditLogPage() {
  const { get } = useApi();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  const [facets, setFacets] = useState({ actions: [], resource_types: [], users: [] });
  const [filters, setFilters] = useState({
    action: ALL, resource_type: ALL, user_id: ALL,
    start_date: '', end_date: '', search: '',
  });
  // Debounced search so a 250 ms gap between keystrokes triggers the fetch.
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setFilters(f => ({ ...f, search: searchInput })), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [expanded, setExpanded] = useState({});

  const loadFacets = useCallback(async () => {
    try {
      const res = await get('/audit/facets');
      setFacets({
        actions: res.actions || [],
        resource_types: res.resource_types || [],
        users: res.users || [],
      });
    } catch { /* ignore */ }
  }, [get]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.action !== ALL) p.append('action', filters.action);
      if (filters.resource_type !== ALL) p.append('resource_type', filters.resource_type);
      if (filters.user_id !== ALL) p.append('user_id', filters.user_id);
      if (filters.start_date) p.append('start_date', filters.start_date);
      if (filters.end_date) p.append('end_date', filters.end_date);
      if (filters.search) p.append('search', filters.search);
      p.append('skip', String(skip));
      p.append('limit', String(PAGE));
      const res = await get(`/audit?${p.toString()}`);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [get, filters, skip]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => { load(); }, [load]);

  // Reset pagination when filters change
  useEffect(() => { setSkip(0); },
    [filters.action, filters.resource_type, filters.user_id,
     filters.start_date, filters.end_date, filters.search]);

  const clearFilters = () => {
    setFilters({ action: ALL, resource_type: ALL, user_id: ALL,
                 start_date: '', end_date: '', search: '' });
    setSearchInput('');
  };
  const hasFilters = useMemo(() => (
    filters.action !== ALL || filters.resource_type !== ALL ||
    filters.user_id !== ALL || filters.start_date || filters.end_date || filters.search
  ), [filters]);

  const userById = useMemo(
    () => Object.fromEntries(facets.users.map(u => [u.user_id, u])),
    [facets.users]
  );

  return (
    <div className="space-y-5" data-testid="page-audit-log">
      <div className="flex items-center gap-3">
        <Shield className="w-7 h-7 text-muted-foreground" />
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="audit-heading">
            Audit Log
          </h1>
          <p className="text-sm text-muted-foreground">
            Every change to trainees, batches, fields, assessments and attendance is recorded here.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-sm font-semibold">Filters</h3>
          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters}
                    className="ml-auto" data-testid="audit-clear-filters">
              <X className="w-3 h-3 mr-1" /> Clear all
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchInput}
                     onChange={e => setSearchInput(e.target.value)}
                     placeholder="Action or resource ID"
                     className="pl-8"
                     data-testid="audit-search" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <Select value={filters.action}
                    onValueChange={v => setFilters({ ...filters, action: v })}>
              <SelectTrigger data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All actions</SelectItem>
                {facets.actions.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Resource</Label>
            <Select value={filters.resource_type}
                    onValueChange={v => setFilters({ ...filters, resource_type: v })}>
              <SelectTrigger data-testid="audit-resource-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {facets.resource_types.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">User</Label>
            <Select value={filters.user_id}
                    onValueChange={v => setFilters({ ...filters, user_id: v })}>
              <SelectTrigger data-testid="audit-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All users</SelectItem>
                {facets.users.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name} ({u.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2 md:col-span-2 lg:col-span-1">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.start_date}
                     onChange={e => setFilters({ ...filters, start_date: e.target.value })}
                     data-testid="audit-start-date" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.end_date}
                     onChange={e => setFilters({ ...filters, end_date: e.target.value })}
                     data-testid="audit-end-date" />
            </div>
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card data-testid="audit-results-card">
        <div className="p-3 border-b text-xs text-muted-foreground flex justify-between items-center">
          <span data-testid="audit-result-count">
            {loading ? 'Loading…' : `${total.toLocaleString()} event${total === 1 ? '' : 's'} matching`}
          </span>
          {total > PAGE && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline"
                      disabled={skip === 0 || loading}
                      onClick={() => setSkip(Math.max(0, skip - PAGE))}
                      data-testid="audit-prev-page">Previous</Button>
              <span>{skip + 1}–{Math.min(skip + PAGE, total)} of {total.toLocaleString()}</span>
              <Button size="sm" variant="outline"
                      disabled={skip + PAGE >= total || loading}
                      onClick={() => setSkip(skip + PAGE)}
                      data-testid="audit-next-page">Next</Button>
            </div>
          )}
        </div>
        {loading ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="p-12 text-center" data-testid="audit-empty-state">
            <Shield className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-display text-lg font-semibold">No events</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try widening your filters.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {items.map(row => {
              const isOpen = !!expanded[row.log_id];
              const userMeta = userById[row.user_id];
              return (
                <div key={row.log_id} data-testid={`audit-row-${row.log_id}`}>
                  <button
                    onClick={() => setExpanded({ ...expanded, [row.log_id]: !isOpen })}
                    className="w-full text-left p-3 hover:bg-muted/40 flex items-start gap-3 transition"
                  >
                    <span className="mt-0.5 shrink-0">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={`${actionTone(row.action)} text-xs`}>
                          {row.action}
                        </Badge>
                        <span className="text-xs text-muted-foreground">on</span>
                        <Badge variant="outline" className="text-xs">{row.resource_type}</Badge>
                        <span className="text-xs font-mono text-muted-foreground truncate"
                              title={row.resource_id || ''}>
                          {row.resource_id?.slice(0, 12)}
                          {row.resource_id?.length > 12 ? '…' : ''}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>by <strong>{row.user_name || userMeta?.name || row.user_email || (row.user_id ? `User ${row.user_id.slice(0, 8)}` : 'Deleted user')}</strong></span>
                        {row.ip_address && <><span>·</span><span>{row.ip_address}</span></>}
                        <span>·</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {relativeTime(row.created_at)}
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0 hidden sm:block">
                      {new Date(row.created_at).toLocaleString()}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 -mt-1 ml-7" data-testid={`audit-row-details-${row.log_id}`}>
                      <pre className="text-xs bg-muted/50 rounded p-3 overflow-x-auto font-mono">
                        {JSON.stringify(row.details || {}, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
